// src/reports/BinDefinitionModal.jsx
// Define bins… for one warehouse (spec §4). Used from Settings → Picking Portal
// (admin) and from the Bin Locations Report header (admin + site_manager).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, TextField, BlockStack, InlineStack, Button, Text, Banner } from '@shopify/polaris';
import { useAuthFetch } from '../AuthComponent';

function alphaToNum(s) { let n = 0; for (const ch of s.toUpperCase()) n = n * 26 + (ch.charCodeAt(0) - 64); return n; }
function numToAlpha(n) { let s = ''; while (n > 0) { const r = (n - 1) % 26; s = String.fromCharCode(65 + r) + s; n = Math.floor((n - 1) / 26); } return s; }
function expandRange(lo, hi) {
    if (/^\d+$/.test(lo) && /^\d+$/.test(hi)) {
        const w = lo.startsWith('0') && lo.length > 1 ? lo.length : 0; const out = [];
        for (let i = parseInt(lo, 10); i <= parseInt(hi, 10); i++) out.push(String(i).padStart(w, '0'));
        return out;
    }
    if (/^[A-Za-z]+$/.test(lo) && /^[A-Za-z]+$/.test(hi)) { const out = []; for (let i = alphaToNum(lo); i <= alphaToNum(hi); i++) out.push(numToAlpha(i)); return out; }
    throw new Error(`bad range {${lo}..${hi}}`);
}
export function countGridLine(line) {
    let n = 1; const re = /\{([A-Za-z0-9]+)\.\.([A-Za-z0-9]+)\}/g; let m;
    while ((m = re.exec(line))) n *= expandRange(m[1], m[2]).length;
    return n;
}

export default function BinDefinitionModal({ open, onClose, storeKey, locationId, locationName, onSaved }) {
    const fetchAuth = useAuthFetch();
    const [form, setForm] = useState({ pattern: '', aisle_group: '', grid: '', special: '', labels: '' });
    const [meta, setMeta] = useState({ metafield_key: '', level: '' });
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState(null);

    const load = useCallback(async () => {
        if (!open || !locationId) return;
        setBusy(true); setMsg(null);
        try {
            const r = await fetchAuth(`/api/reports/bin-locations/definition?store=${storeKey}&location_id=${locationId}`);
            const j = await r.json();
            if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
            const b = j.bins || {};
            setMeta({ metafield_key: j.metafield_key, level: j.level });
            setForm({ pattern: b.pattern || '', aisle_group: b.aisle_group ? String(b.aisle_group) : '',
                      grid: (b.grid || []).join('\n'), special: (b.special || []).join('\n'), labels: (b.labels || []).join(', ') });
        } catch (e) { setMsg({ tone: 'critical', text: e.message }); }
        finally { setBusy(false); }
    }, [open, locationId, storeKey, fetchAuth]);
    useEffect(() => { load(); }, [load]);

    const gridCount = useMemo(() => {
        try { return form.grid.split('\n').map(s => s.trim()).filter(Boolean).reduce((a, l) => a + countGridLine(l), 0); }
        catch { return null; }
    }, [form.grid]);
    const regexOk = useMemo(() => { try { if (form.pattern) new RegExp(form.pattern, 'i'); return true; } catch { return false; } }, [form.pattern]);

    const suggest = async () => {
        setBusy(true); setMsg(null);
        try {
            const r = await fetchAuth('/api/reports/bin-locations/suggest', { method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ store: storeKey, location_id: locationId }) });
            const j = await r.json();
            if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
            const s = j.suggestion;
            setForm({ pattern: s.pattern || '', aisle_group: s.aisle_group ? String(s.aisle_group) : '',
                      grid: (s.grid || []).join('\n'), special: (s.special || []).join('\n'), labels: (s.labels || []).join(', ') });
            setMsg({ tone: 'info', text: `Suggested from ${j.sampled.toLocaleString()} bin values. Check the ranges, then Save.` });
        } catch (e) { setMsg({ tone: 'critical', text: e.message }); }
        finally { setBusy(false); }
    };

    const save = async () => {
        if (!regexOk) return setMsg({ tone: 'critical', text: 'The regex does not compile.' });
        setBusy(true); setMsg(null);
        try {
            const bins = { pattern: form.pattern.trim(), aisle_group: form.aisle_group ? parseInt(form.aisle_group, 10) : null,
                           grid: form.grid.split('\n').map(s => s.trim()).filter(Boolean),
                           special: form.special.split('\n').map(s => s.trim()).filter(Boolean),
                           labels: form.labels.split(',').map(s => s.trim()).filter(Boolean) };
            const r = await fetchAuth('/api/reports/bin-locations/definition', { method: 'PUT', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ store: storeKey, location_id: locationId, bins }) });
            const j = await r.json();
            if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
            setMsg({ tone: 'success', text: `Saved. ${j.grid_bins.toLocaleString()} bins in the grid. The next snapshot (3:30 AM, or Refresh now) uses it.` });
            onSaved && onSaved();
        } catch (e) { setMsg({ tone: 'critical', text: e.message }); }
        finally { setBusy(false); }
    };

    return (
        <Modal open={open} onClose={onClose} title={`Define bins — ${locationName || ''}`} size="large"
               primaryAction={{ content: 'Save', onAction: save, loading: busy, disabled: !regexOk }}
               secondaryActions={[{ content: 'Suggest from current bins', onAction: suggest, disabled: busy }, { content: 'Cancel', onAction: onClose }]}>
            <Modal.Section>
                {/* 2026-09-13 (Eric): everything fits without the modal scrolling —
                    the two text boxes cap their height and scroll internally
                    (.bin-def-modal textarea in styles.css). */}
                <div className="bin-def-modal">
                <BlockStack gap="300">
                    {msg && <Banner tone={msg.tone}>{msg.text}</Banner>}
                    <Text tone="subdued" variant="bodySm">Metafield <code>{meta.metafield_key}</code> ({meta.level} level). The regex says what a bin looks like; the grid lines list every bin that exists so empty bins can be counted; specials are bins outside the ranges.</Text>
                    <div className="bin-def-modal__row">
                        <TextField label="Regex a bin must match" value={form.pattern} onChange={v => setForm(f => ({ ...f, pattern: v }))}
                                   autoComplete="off" monospaced error={!regexOk ? 'Not a valid regular expression' : undefined}
                                   helpText='e.g. ^(\d+)-(\d+)-(\d+)-(\d+)-(\d+)$ or ^([A-Z]{1,2})-(\d{2})$' />
                        <TextField label="Aisle = capture group #" type="number" value={form.aisle_group} onChange={v => setForm(f => ({ ...f, aisle_group: v }))}
                                   autoComplete="off" helpText="Blank hides the aisle table." />
                    </div>
                    <div className="bin-def-modal__row">
                        <TextField label="Grid (one range line per row)" value={form.grid} onChange={v => setForm(f => ({ ...f, grid: v }))} multiline={8} monospaced autoComplete="off"
                                   helpText={`{1..27} number range ({01..30} keeps padding); {A..AD} letter range. ${gridCount === null ? 'A range line is malformed.' : `${gridCount.toLocaleString()} bins.`}`} />
                        <TextField label="Special bins (one per line)" value={form.special} onChange={v => setForm(f => ({ ...f, special: v }))} multiline={8} monospaced autoComplete="off"
                                   helpText="Bins outside the ranges." />
                    </div>
                    <TextField label="Segment labels (comma separated)" value={form.labels} onChange={v => setForm(f => ({ ...f, labels: v }))} autoComplete="off"
                               placeholder="Building, Aisle, Bay, Shelf, Bin" />
                </BlockStack>
                </div>
            </Modal.Section>
        </Modal>
    );
}
